import React, { useMemo } from 'react';
import * as yup from 'yup';
import { each } from 'lodash';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { BiArrowBack } from 'react-icons/bi';

import Card from 'partial/components/Card';
import Button from 'partial/components/Button';
import Form, { ErrorLabel } from 'partial/form/Form';
import { useCreateModal } from 'partial/components/Modal';
import FormSignature from 'partial/form/FormSignature';
import {
  useGetBusinessInfo,
  useGetGeneralInfo,
} from 'core-modules/merchants/hooks';
import FormCheckbox from 'partial/form/FormCheckbox';
import PrivacyPolicyModal from 'modules/auth/modals/PrivacyPolicyModal';
import TermsAndConditionModal from 'modules/auth/modals/TermsAndConditionModal';

import { useSubmitApplication } from '../hooks';

const validationSchema = yup.object().shape({
  signature_url: yup
    .string()
    .required(
      'Signature is required. Make sure you clicked "Confirm" before submitting.'
    ),
  review: yup
    .bool()
    .oneOf([true], 'You must confirm that the submitted fields are correct'),
});

const MAP_BUSINESS_TYPE_LABEL = {
  CORPORATION: 'Corporation',
  SOLE_PROPRIETORSHIP: 'Sole proprietorship/Partnership',
  INDIVIDUAL: 'Individual',
  GOVERNMENT: 'Government',
};

function StatementOfAcceptance({ onBack, onNext, onReviewAgain }) {
  const [isLoading, submit] = useSubmitApplication();
  const [, businessData] = useGetBusinessInfo();
  const [, generalInfoData] = useGetGeneralInfo();
  const ownerInfo = useMemo(() => {
    const homeAddressFormat = `${
      businessData?.unit_no ? `${businessData?.unit_no}, ` : ''
    }${businessData?.house_no ? `${businessData?.house_no}, ` : ''}${
      businessData?.building_name ? `${businessData?.building_name}, ` : ''
    }${businessData?.street ? `${businessData?.street}, ` : ''}${
      businessData?.district ? `${businessData?.district}, ` : ''
    }${businessData?.postal_code ? `${businessData?.postal_code}` : ''}`;

    const businessAddressFormat = `${
      generalInfoData?.unit_no ? `${generalInfoData?.unit_no}, ` : ''
    }${generalInfoData?.house_no ? `${generalInfoData?.house_no}, ` : ''}${
      generalInfoData?.building_name
        ? `${generalInfoData?.building_name}, `
        : ''
    }${generalInfoData?.street ? `${generalInfoData?.street}, ` : ''}${
      generalInfoData?.district ? `${generalInfoData?.district}, ` : ''
    }${generalInfoData?.postal_code ? `${generalInfoData?.postal_code}` : ''}`;

    return {
      first_name: businessData?.first_name || '',
      last_name: businessData?.last_name || '',
      home_address: homeAddressFormat || '',
      business_address: businessAddressFormat || '',
      business_name: generalInfoData?.name || '',
      business_type: MAP_BUSINESS_TYPE_LABEL[generalInfoData?.business_type],
    };
  }, [businessData, generalInfoData]);

  const [validateOnChange, setValidateOnChange] = React.useState(false);
  const { values, setFieldValue, handleSubmit, errors } = useFormik({
    initialValues: {
      signature_url: '',
      review: false,
    },
    validationSchema,
    onSubmit: async (form, { setFieldError }) => {
      submit(
        form,
        onNext, // onSuccess
        (err) => {
          each(err, (v, k) => {
            setFieldError(k, v);
          });
        }
      );
    },
    validateOnChange,
  });
  const handleReview = (e) => {
    e.preventDefault();
    onReviewAgain();
  };

  const modal = useCreateModal();

  const handleTermsOfUse = () => {
    modal({
      content: (close) => <TermsAndConditionModal onClose={close} />,
    });
  };

  const handlePrivacyDetails = () => {
    modal({
      content: (close) => <PrivacyPolicyModal onClose={close} />,
    });
  };

  return (
    <Card className="relative">
      <span className="absolute top-0 left-0 m-4">
        <Button
          transparent
          size="lg"
          icon={BiArrowBack}
          onClick={() => onBack()}
        >
          Back
        </Button>
      </span>
      <Form
        onSubmit={handleSubmit}
        error={errors}
        noValidate
        className="space-y-3"
      >
        <div className="p-12">
          <div className="space-y-4">
            <div className="text-center space-y-3 mx-auto">
              <div className="title-h1 text-center">
                Statement of Acceptance
              </div>
              <p className="text-gray-500">
                Please review and sign below to complete your request for
                activation
              </p>
            </div>
            <hr />
            <div>
              <p className="text-gray-500">
                I,{' '}
                <span className="font-semibold text-black capitalize">
                  {ownerInfo?.first_name} {ownerInfo?.last_name}
                </span>
                , of legal age, with residence at{' '}
                <span className="font-semibold text-black capitalize">
                  {ownerInfo?.home_address}
                </span>
                , depose and state that:
              </p>
              <ol className="mt-4 list-decimal grid grid-flow-row gap-4">
                <li className="text-gray-500">
                  I am the authorized representative of the{' '}
                  <span className="font-semibold capitalize">
                    {ownerInfo?.business_name}
                  </span>
                  , a/an <span>{ownerInfo?.business_type}</span>, located at{' '}
                  <span className="font-semibold capitalize">
                    {ownerInfo?.business_address}
                  </span>
                  ;
                </li>
                <li className="text-gray-500">
                  On behalf of the business, I have fully read, understood and
                  accept the{' '}
                  <span
                    tabIndex={0}
                    role="button"
                    className="text-primary-500 cursor-pointer"
                    onClick={handleTermsOfUse}
                    onKeyDown={handleTermsOfUse}
                  >
                    DST Terms and Conditions of Services
                  </span>{' '}
                  and{' '}
                  <span
                    tabIndex={0}
                    role="button"
                    className="text-primary-500 cursor-pointer"
                    onClick={handlePrivacyDetails}
                    onKeyDown={handlePrivacyDetails}
                  >
                    Privacy Policy
                  </span>{' '}
                  and agree to be bound by these agreements;
                </li>
                <li className="text-gray-500">
                  These agreements constitute the entire agreement between DST
                  Biller Portal and the business of which I am the
                  representative. This supersedes any and all prior memoranda of
                  agreements, whether oral or in writing;
                </li>
                <li className="text-gray-500">
                  The definitive versions of these agreements are the ones found
                  on the{' '}
                  <Link
                    className="text-primary-500 font-normal"
                    to={{
                      pathname: process.env.REACT_APP_WEBSITE_URL,
                    }}
                    target="_blank"
                  >
                    DST Biller Portal website
                  </Link>
                  ; and,
                </li>
                <li className="text-gray-500">
                  DST Biller Portal reserves the right to change these
                  agreements from time to time, with or without notice. The
                  latest{' '}
                  <span
                    tabIndex={0}
                    role="button"
                    className="text-primary-500 cursor-pointer"
                    onClick={handleTermsOfUse}
                    onKeyDown={handleTermsOfUse}
                  >
                    DST Terms and Conditions of Services
                  </span>{' '}
                  and{' '}
                  <span
                    tabIndex={0}
                    role="button"
                    className="text-primary-500 cursor-pointer"
                    onClick={handlePrivacyDetails}
                    onKeyDown={handlePrivacyDetails}
                  >
                    Privacy Policy
                  </span>{' '}
                  are always posted on the{' '}
                  <Link
                    className="text-primary-500 font-normal"
                    to={{
                      pathname: process.env.REACT_APP_WEBSITE_URL,
                    }}
                    target="_blank"
                  >
                    DST Biller Portal website
                  </Link>
                  . For any changes to these agreements, our continued use of
                  the Service (as defined in these agreements) constitutes our
                  consent to the new agreements.
                </li>
              </ol>
            </div>

            <FormSignature
              name="signature_url"
              onSetFieldValue={setFieldValue}
              value={values.signature_url}
            />
            <ErrorLabel name="signature_url" />
            <hr />
            <div>
              <div className="flex flex-col">
                <div className="flex items-center">
                  <FormCheckbox
                    id="review"
                    name="review"
                    value={values.review}
                    label=""
                    onSetFieldValue={setFieldValue}
                  />
                  <div className="text-gray-600">
                    <label htmlFor="review" className="cursor-pointer">
                      I confirm that the submitted fields are correct.{' '}
                      <a href="/" onClick={handleReview}>
                        Review application
                      </a>
                    </label>
                  </div>
                </div>
                <ErrorLabel name="review" />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                primary
                disabled={isLoading}
                onClick={() => setValidateOnChange(true)}
              >
                Submit All Requirements
              </Button>
            </div>
          </div>
        </div>
      </Form>
    </Card>
  );
}

StatementOfAcceptance.propTypes = {
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onReviewAgain: PropTypes.func.isRequired,
};

export default StatementOfAcceptance;
